import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/store";
import { Navigate, Link } from "react-router-dom";
import { api, brl, API_BASE } from "../lib/api";
import { Package, Users, Calendar, DollarSign, ShoppingBag, PlusCircle, Pencil, Trash2, LogOut, Video, Tag, Bell, MessageCircle, Send, Upload } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  ["dashboard", "Dashboard", DollarSign],
  ["products", "Produtos", Package],
  ["videos", "Vídeos", Video],
  ["orders", "Pedidos", ShoppingBag],
  ["appointments", "Agendamentos", Calendar],
  ["customers", "Clientes", Users],
  ["reminders", "Lembretes", Bell],
  ["coupons", "Cupons", Tag],
];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  if (loading) return <div className="py-24 text-center">Carregando…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/entrar" replace />;

  const T = { dashboard: Dashboard, products: Products, videos: Videos, orders: Orders, appointments: Appointments, customers: Customers, reminders: Reminders, coupons: Coupons }[tab];

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white" data-testid="admin-root">
      <div className="grid lg:grid-cols-[240px_1fr]">
        <aside className="p-6 border-r border-white/10 min-h-screen">
          <Link to="/" className="font-display text-2xl">Eterniza <span className="font-script text-[#D4AF37]">imãs</span></Link>
          <div className="text-xs text-white/50 mt-1">Painel administrativo</div>
          <nav className="mt-8 flex flex-col gap-1">
            {TABS.map(([k, l, I]) => (
              <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-left transition-colors ${
                  tab === k ? "bg-[#D4AF37] text-[#0D1B2A]" : "hover:bg-white/5"
                }`}>
                <I size={16} /> {l}
              </button>
            ))}
          </nav>
          <button onClick={logout} className="mt-8 text-xs text-white/60 hover:text-white flex items-center gap-2"><LogOut size={14} /> Sair</button>
        </aside>
        <main className="p-6 md:p-8 bg-[#F7F2E7] text-[#0D1B2A]"><T /></main>
      </div>
    </div>
  );
}

function Dashboard() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)); }, []);
  const cards = [
    ["Pedidos", s.orders ?? "-", ShoppingBag],
    ["Aprovados", s.approved_orders ?? "-", DollarSign],
    ["Receita", brl(s.revenue || 0), DollarSign],
    ["Clientes", s.customers ?? "-", Users],
    ["Instagram", s.instagram_followers ?? "-", Bell],
    ["Agendamentos", s.appointments ?? "-", Calendar],
  ];
  return (
    <div data-testid="admin-dashboard">
      <div className="eyebrow">Visão geral</div>
      <h1 className="font-display text-4xl mt-2">Bem-vindo(a)</h1>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map(([t, v, I]) => (
          <div key={t} className="bg-white rounded-2xl p-5 border border-[#0D1B2A]/5">
            <I size={18} className="text-[#D4AF37]" />
            <div className="text-sm text-[#6B7280] mt-2">{t}</div>
            <div className="font-display text-3xl mt-1">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Products() {
  const [list, setList] = useState([]);
  const [videos, setVideos] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/products").then(({ data }) => setList(data));
  useEffect(() => { load(); api.get("/videos").then(({ data }) => setVideos(data)); }, []);
  const empty = { name: "", slug: "", category: "imas", description: "", price: 0, image_url: "", gallery: [], video_ids: [], story_cards: [], active: true, allow_photo_upload: true, options: [], highlight: "", tagline: "" };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, editing);
      else await api.post("/admin/products", editing);
      toast.success("Salvo!"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const del = async (id) => { if (!window.confirm("Excluir?")) return; await api.delete(`/admin/products/${id}`); toast.success("Removido"); load(); };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">Produtos</h1>
        <button onClick={() => setEditing(empty)} data-testid="admin-new-product" className="btn-gold inline-flex items-center gap-2"><PlusCircle size={16} /> Novo</button>
      </div>
      <div className="mt-6 bg-white rounded-2xl border border-[#0D1B2A]/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-[#6B7280]">
            <tr><th className="p-4">Nome</th><th>Categoria</th><th>Preço</th><th>Vídeos</th><th className="text-right pr-4">Ações</th></tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-t border-[#0D1B2A]/5">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={p.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    <span className="font-medium">{p.name}</span>
                  </div>
                </td>
                <td>{p.category}</td>
                <td>{brl(p.price)}</td>
                <td>{p.video_ids?.length || 0}</td>
                <td className="text-right pr-4">
                  <button onClick={() => setEditing(p)} className="p-2 hover:text-[#D4AF37]"><Pencil size={15} /></button>
                  <button onClick={() => del(p.id)} className="p-2 hover:text-[#E66E6E]"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl">{editing.id ? "Editar" : "Novo"} produto</div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <Field l="Nome" v={editing.name} onC={(v) => setEditing({ ...editing, name: v })} />
              <Field l="Slug" v={editing.slug} onC={(v) => setEditing({ ...editing, slug: v })} />
              <Field l="Categoria" v={editing.category} onC={(v) => setEditing({ ...editing, category: v })} />
              <Field l="Preço" type="number" v={editing.price} onC={(v) => setEditing({ ...editing, price: Number(v) })} />
              <div className="sm:col-span-2"><Field l="URL imagem principal" v={editing.image_url} onC={(v) => setEditing({ ...editing, image_url: v })} /></div>
              <Field l="Tagline" v={editing.tagline} onC={(v) => setEditing({ ...editing, tagline: v })} />
              <Field l="Destaque (label)" v={editing.highlight} onC={(v) => setEditing({ ...editing, highlight: v })} />
              <div className="sm:col-span-2">
                <label className="text-sm">Descrição</label>
                <textarea className="input-luxe mt-1" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm">Vídeos do produto</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {videos.map((v) => {
                    const selected = editing.video_ids?.includes(v.id);
                    return (
                      <button key={v.id} type="button" onClick={() =>
                        setEditing({ ...editing, video_ids: selected ? editing.video_ids.filter((x) => x !== v.id) : [...(editing.video_ids || []), v.id] })
                      } className={`text-xs px-3 py-1.5 rounded-full ${selected ? "bg-[#D4AF37] text-[#0D1B2A]" : "bg-[#F7F2E7] text-[#0D1B2A]"}`}>
                        {v.original_filename || v.id.slice(0, 8)}
                      </button>
                    );
                  })}
                  {videos.length === 0 && <div className="text-xs text-[#6B7280]">Nenhum vídeo. Envie na aba "Vídeos".</div>}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm">Story cards (como funciona)</label>
                {(editing.story_cards || []).map((c, i) => (
                  <div key={i} className="grid sm:grid-cols-[1fr_1.5fr_2fr_40px] gap-2 mt-2">
                    <input placeholder="Título" className="input-luxe" value={c.title} onChange={(e) => setEditing({ ...editing, story_cards: editing.story_cards.map((s, idx) => idx === i ? { ...s, title: e.target.value } : s) })} />
                    <input placeholder="URL imagem" className="input-luxe" value={c.image} onChange={(e) => setEditing({ ...editing, story_cards: editing.story_cards.map((s, idx) => idx === i ? { ...s, image: e.target.value } : s) })} />
                    <input placeholder="Descrição" className="input-luxe" value={c.description} onChange={(e) => setEditing({ ...editing, story_cards: editing.story_cards.map((s, idx) => idx === i ? { ...s, description: e.target.value } : s) })} />
                    <button type="button" onClick={() => setEditing({ ...editing, story_cards: editing.story_cards.filter((_, idx) => idx !== i) })} className="text-[#E66E6E]"><Trash2 size={16} /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setEditing({ ...editing, story_cards: [...(editing.story_cards || []), { title: "", description: "", image: "" }] })} className="text-sm text-[#D4AF37] mt-2 inline-flex items-center gap-1"><PlusCircle size={14} /> adicionar card</button>
              </div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Ativo</div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={editing.allow_photo_upload} onChange={(e) => setEditing({ ...editing, allow_photo_upload: e.target.checked })} /> Permitir upload foto</div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-outline-navy">Cancelar</button>
              <button onClick={save} data-testid="admin-save-product" className="btn-gold">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Videos() {
  const [list, setList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const load = () => api.get("/videos").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const token = localStorage.getItem("eterniza_token");
      const r = await fetch(`${API_BASE}/admin/videos`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      toast.success("Vídeo enviado"); load();
    } catch { toast.error("Falha no upload"); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div data-testid="admin-videos">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">Vídeos</h1>
        <label className="btn-gold inline-flex items-center gap-2 cursor-pointer">
          <Upload size={16} /> {uploading ? "Enviando..." : "Enviar vídeo"}
          <input ref={inputRef} data-testid="video-upload-input" type="file" accept="video/*" className="hidden" onChange={upload} />
        </label>
      </div>
      <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
        {list.map((v) => (
          <div key={v.id} className="bg-white rounded-2xl overflow-hidden border border-[#0D1B2A]/5">
            <video src={`${API_BASE}/videos/${v.id}`} className="w-full aspect-video object-cover bg-black" controls preload="metadata" />
            <div className="p-3 text-sm">
              <div className="font-medium truncate">{v.original_filename}</div>
              <div className="text-xs text-[#6B7280]">{(v.size / 1024 / 1024).toFixed(1)} MB</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Orders() {
  const [list, setList] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/orders")
      .then(({ data }) => { setList(Array.isArray(data) ? data : []); setLoading(false); })
      .catch((e) => { setError(e?.response?.data?.detail || e?.message || "Erro ao carregar pedidos"); setLoading(false); });
  }, []);
  const safeDate = (v) => { try { return v ? new Date(v).toLocaleString("pt-BR") : "—"; } catch { return "—"; } };
  return (
    <div data-testid="admin-orders">
      <h1 className="font-display text-4xl">Pedidos</h1>
      {loading && <div className="mt-8 text-center text-[#6B7280]">Carregando…</div>}
      {error && <div className="mt-6 bg-red-50 border border-red-200 text-red-800 rounded-2xl p-5 text-sm">Erro: {error}</div>}
      {!loading && !error && (
      <div className="mt-6 grid gap-4">
        {list.map((o) => {
          const id = o?.id || "";
          const items = Array.isArray(o?.items) ? o.items : [];
          const customer = o?.customer || {};
          const total = o?.total_brl ?? o?.total ?? 0;
          const status = o?.status || "—";
          return (
          <div key={id || Math.random()} className="bg-white rounded-2xl p-5 border border-[#0D1B2A]/5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-medium">{customer.name || "Cliente"} · {customer.email || ""}</div>
                <div className="text-xs text-[#6B7280]">#{id.slice(0, 8)} · {safeDate(o?.created_at)}</div>
                {o?.coupon_code && <div className="text-xs text-[#D4AF37] mt-1">Cupom: {o.coupon_code}</div>}
              </div>
              <div className="text-right">
                <span className={`text-xs px-2 py-1 rounded-full ${status === "approved" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>{status}</span>
                <div className="font-display text-xl mt-1">{brl(total)}</div>
              </div>
            </div>
            {items.length > 0 && (
              <div className="mt-3 text-sm text-[#6B7280]">
                {items.map((i, idx) => <div key={idx}>· {i?.quantity ?? 1}× {i?.name || "Item"}</div>)}
              </div>
            )}
            {o?.admin_wa_link && (
              <a href={o.admin_wa_link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm bg-[#25D366] text-white px-4 py-2 rounded-full">
                <MessageCircle size={14} /> Abrir no WhatsApp
              </a>
            )}
          </div>
        );})}
        {list.length === 0 && <div className="text-center text-[#6B7280] py-12">Nenhum pedido ainda.</div>}
      </div>
      )}
    </div>
  );
}

function Appointments() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/appointments").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.put(`/admin/appointments/${id}?status=${status}`); toast.success("Atualizado"); load(); };
  return (
    <div data-testid="admin-appointments">
      <h1 className="font-display text-4xl">Agendamentos</h1>
      <div className="mt-6 grid gap-4">
        {list.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl p-5 border border-[#0D1B2A]/5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-medium">{a.customer_name} · {a.appointment_type}</div>
                <div className="text-xs text-[#6B7280]">{a.customer_email} · {a.customer_phone}</div>
                <div className="text-sm mt-2">{new Date(a.scheduled_at).toLocaleString("pt-BR")}</div>
                {a.notes && <div className="text-xs text-[#6B7280] mt-1 italic">"{a.notes}"</div>}
              </div>
              <select value={a.status} onChange={(e) => setStatus(a.id, e.target.value)} className="input-luxe py-1.5 text-sm">
                <option value="requested">Solicitado</option>
                <option value="confirmed">Confirmado</option>
                <option value="done">Concluído</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="text-center text-[#6B7280] py-12">Nenhum agendamento ainda.</div>}
      </div>
    </div>
  );
}

function Customers() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/customers").then(({ data }) => setList(data)); }, []);
  return (
    <div data-testid="admin-customers">
      <h1 className="font-display text-4xl">Clientes</h1>
      <div className="mt-6 bg-white rounded-2xl border border-[#0D1B2A]/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-[#6B7280]">
            <tr><th className="p-4">Nome</th><th>E-mail</th><th>WhatsApp</th><th>Aniversário</th><th>IG</th><th>Cadastro</th></tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-[#0D1B2A]/5">
                <td className="p-4 font-medium">{c.name}</td>
                <td>{c.email}</td>
                <td>{c.phone}</td>
                <td>{c.birthday || "—"}</td>
                <td>{c.instagram_followed ? "✓" : "—"}</td>
                <td className="text-[#6B7280]">{new Date(c.created_at).toLocaleDateString("pt-BR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Reminders() {
  const [data, setData] = useState({ upcoming: [], dormant: [] });
  const [sending, setSending] = useState(null);
  const load = () => api.get("/admin/reminders").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  const sendEmail = async (customer_id, label, kind) => {
    setSending(customer_id + kind);
    try { await api.post("/admin/reminders/send-email", { customer_id, label, kind }); toast.success("E-mail enviado"); }
    catch { toast.error("Erro ao enviar"); }
    finally { setSending(null); }
  };

  return (
    <div data-testid="admin-reminders">
      <h1 className="font-display text-4xl">Lembretes & Recorrência</h1>

      <section className="mt-8">
        <div className="eyebrow">Próximas datas (30 dias)</div>
        <div className="mt-3 space-y-3">
          {data.upcoming.map((u) => (
            <div key={u.customer_id + u.label} className="bg-white rounded-2xl p-4 border border-[#0D1B2A]/5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-medium">{u.customer_name}</div>
                <div className="text-xs text-[#6B7280]">{u.label} em {u.days_until} dias · {u.email}</div>
              </div>
              <div className="flex gap-2">
                <a href={u.wa_link} target="_blank" rel="noreferrer" className="text-sm inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full"><MessageCircle size={14} /> WhatsApp</a>
                <button onClick={() => sendEmail(u.customer_id, u.label, u.kind)} disabled={sending === u.customer_id + u.kind} className="text-sm inline-flex items-center gap-2 btn-gold"><Send size={14} /> {sending === u.customer_id + u.kind ? "..." : "Enviar e-mail"}</button>
              </div>
            </div>
          ))}
          {data.upcoming.length === 0 && <div className="text-sm text-[#6B7280]">Sem datas próximas nos próximos 30 dias.</div>}
        </div>
      </section>

      <section className="mt-10">
        <div className="eyebrow">Recorrência · Prontos para nova compra (60+ dias)</div>
        <div className="mt-3 space-y-3">
          {data.dormant.map((d) => (
            <div key={d.customer_id} className="bg-white rounded-2xl p-4 border border-[#0D1B2A]/5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-medium">{d.customer_name}</div>
                <div className="text-xs text-[#6B7280]">Último pedido há {d.days_since_last} dias · {d.email}</div>
              </div>
              <div className="flex gap-2">
                <a href={d.wa_link} target="_blank" rel="noreferrer" className="text-sm inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full"><MessageCircle size={14} /> WhatsApp</a>
                <button onClick={() => sendEmail(d.customer_id, "presente especial", "dormant")} className="text-sm inline-flex items-center gap-2 btn-gold"><Send size={14} /> E-mail win-back</button>
              </div>
            </div>
          ))}
          {data.dormant.length === 0 && <div className="text-sm text-[#6B7280]">Nenhum cliente dormente por enquanto.</div>}
        </div>
      </section>
    </div>
  );
}

function Coupons() {
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", percent_off: 10, amount_off: 0, valid_days: 30, max_uses: 0, minimum_total: 0, description: "" });
  const load = () => api.get("/admin/coupons").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/admin/coupons", form); toast.success("Cupom criado"); setCreating(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const del = async (code) => { if (!window.confirm(`Excluir ${code}?`)) return; await api.delete(`/admin/coupons/${code}`); load(); };

  return (
    <div data-testid="admin-coupons">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">Cupons</h1>
        <button onClick={() => setCreating(true)} data-testid="admin-new-coupon" className="btn-gold inline-flex items-center gap-2"><PlusCircle size={16} /> Novo cupom</button>
      </div>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => (
          <div key={c.code} className="bg-white rounded-2xl p-5 border border-[#0D1B2A]/5">
            <div className="text-[10px] tracking-widest text-[#6B7280]">{c.kind?.toUpperCase() || "CUPOM"}</div>
            <div className="font-display text-2xl mt-1">{c.code}</div>
            <div className="text-sm text-[#6B7280] mt-1">{c.description}</div>
            <div className="mt-3 text-sm">
              {c.percent_off ? <>Desconto: <b>{c.percent_off}%</b></> : <>Valor: <b>{brl(c.amount_off)}</b></>}
            </div>
            <div className="text-xs text-[#6B7280]">Usos: {c.uses}{c.max_uses ? `/${c.max_uses}` : ""}</div>
            {c.kind === "custom" && <button onClick={() => del(c.code)} className="mt-3 text-xs text-[#E66E6E]">Excluir</button>}
          </div>
        ))}
      </div>
      {creating && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl">Novo cupom</div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <Field l="Código" v={form.code} onC={(v) => setForm({ ...form, code: v.toUpperCase() })} />
              <Field l="% off" type="number" v={form.percent_off} onC={(v) => setForm({ ...form, percent_off: Number(v) })} />
              <Field l="R$ off" type="number" v={form.amount_off} onC={(v) => setForm({ ...form, amount_off: Number(v) })} />
              <Field l="Validade (dias)" type="number" v={form.valid_days} onC={(v) => setForm({ ...form, valid_days: Number(v) })} />
              <Field l="Máx. usos (0=infinito)" type="number" v={form.max_uses} onC={(v) => setForm({ ...form, max_uses: Number(v) })} />
              <Field l="Total mínimo" type="number" v={form.minimum_total} onC={(v) => setForm({ ...form, minimum_total: Number(v) })} />
              <div className="sm:col-span-2"><Field l="Descrição" v={form.description} onC={(v) => setForm({ ...form, description: v })} /></div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="btn-outline-navy">Cancelar</button>
              <button onClick={create} data-testid="save-coupon" className="btn-gold">Criar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ l, v, onC, type = "text" }) => (
  <div>
    <label className="text-sm">{l}</label>
    <input type={type} value={v ?? ""} onChange={(e) => onC(e.target.value)} className="input-luxe mt-1" />
  </div>
);
